import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getMe, UnauthenticatedError, type AuthUser, type MeVenue, type Permission } from './me';

type AuthContextValue = {
  user: AuthUser | null;
  permissions: Permission[];
  /** Venues the user may enter (ADR 0015); source for the workspace switcher + name. */
  venues: MeVenue[];
  isAuthenticated: boolean;
  isLoading: boolean;
  mustChangePassword: boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AUTH_ME_QUERY_KEY = ['auth', 'me'] as const;

export function AuthProvider({ children }: { children?: ReactNode }) {
  const query = useQuery({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: getMe,
    retry: (failureCount, error) => {
      if (error instanceof UnauthenticatedError) return false;
      return failureCount < 1;
    },
    staleTime: 60_000,
  });

  const value = useMemo<AuthContextValue>(
    () => ({
      user: query.data?.user ?? null,
      permissions: query.data?.permissions ?? [],
      venues: query.data?.venues ?? [],
      isAuthenticated: !!query.data?.user,
      isLoading: query.isPending,
      mustChangePassword: query.data?.user?.mustChangePassword ?? false,
    }),
    [query.data, query.isPending],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
